/**
 * The console's icons, as inline SVG.
 *
 * No icon package, for the same reason there is no component library: a
 * dependency needs asking for (CLAUDE.md §10), and sixteen glyphs do not earn
 * one. Emoji were the other option and are wrong here — they render in the
 * platform's own style, at the platform's own weight, and a dashboard built
 * from them reads as a prototype.
 *
 * All are 24×24 line icons on a common grid, stroked with `currentColor` at
 * 1.75 so they sit at the weight of the 600 text beside them. Size comes from
 * CSS (`.nav-link svg`, `.btn svg`), never from a prop, so a glyph can never
 * disagree with its label about how big it is.
 */
import type { SVGProps } from 'react'

type IconProps = SVGProps<SVGSVGElement>

function Svg({ children, ...rest }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {children}
    </svg>
  )
}

export const IconDashboard = (p: IconProps) => (
  <Svg {...p}>
    <rect x="3" y="3" width="7.5" height="8.5" rx="1.5" />
    <rect x="13.5" y="3" width="7.5" height="5.5" rx="1.5" />
    <rect x="3" y="14.5" width="7.5" height="6.5" rx="1.5" />
    <rect x="13.5" y="11.5" width="7.5" height="9.5" rx="1.5" />
  </Svg>
)

export const IconOrders = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4 6.5 12 3l8 3.5v11L12 21l-8-3.5z" />
    <path d="M4 6.5 12 10l8-3.5M12 10v11" />
  </Svg>
)

export const IconProducts = (p: IconProps) => (
  <Svg {...p}>
    <rect x="3" y="6" width="18" height="14" rx="2" />
    <path d="M8 6V4.5A1.5 1.5 0 0 1 9.5 3h5A1.5 1.5 0 0 1 16 4.5V6" />
    <path d="M3 11h18" />
  </Svg>
)

export const IconCategories = (p: IconProps) => (
  <Svg {...p}>
    <rect x="3" y="3" width="7" height="7" rx="1.5" />
    <rect x="14" y="3" width="7" height="7" rx="1.5" />
    <rect x="3" y="14" width="7" height="7" rx="1.5" />
    <rect x="14" y="14" width="7" height="7" rx="1.5" />
  </Svg>
)

export const IconInventory = (p: IconProps) => (
  <Svg {...p}>
    <path d="M3 8.5 12 4l9 4.5v7L12 20l-9-4.5z" />
    <path d="M7.5 6.2v7.1L16.5 18" />
  </Svg>
)

export const IconImport = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 3v11" />
    <path d="m8 10.5 4 3.5 4-3.5" />
    <path d="M4 17v2.5A1.5 1.5 0 0 0 5.5 21h13a1.5 1.5 0 0 0 1.5-1.5V17" />
  </Svg>
)

export const IconRetailers = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4 9.5V20a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V9.5" />
    <path d="M3 6.5 4.5 3h15L21 6.5a3 3 0 0 1-6 0 3 3 0 0 1-6 0 3 3 0 0 1-6 0Z" />
    <path d="M10 21v-5.5h4V21" />
  </Svg>
)

export const IconPartners = (p: IconProps) => (
  <Svg {...p}>
    <rect x="1.5" y="7" width="12" height="9" rx="1.5" />
    <path d="M13.5 10.5H17l3.5 3.5V16h-7z" />
    <circle cx="6" cy="18.5" r="2" />
    <circle cx="17" cy="18.5" r="2" />
  </Svg>
)

export const IconPromotions = (p: IconProps) => (
  <Svg {...p}>
    <path d="M20.5 12.5 12.8 20.2a1.7 1.7 0 0 1-2.4 0l-6.6-6.6a1.7 1.7 0 0 1-.5-1.2V4.7A1.7 1.7 0 0 1 5 3h7.7c.45 0 .88.18 1.2.5l6.6 6.6a1.7 1.7 0 0 1 0 2.4Z" />
    <circle cx="8" cy="8" r="1.4" />
  </Svg>
)

export const IconReports = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4 20V4" />
    <path d="M4 20h16" />
    <path d="M8 20v-6M12.5 20V9M17 20v-9.5" />
  </Svg>
)

export const IconBroadcast = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4 9v6h3.5L14 19.5v-15L7.5 9z" />
    <path d="M17.5 8.5a5 5 0 0 1 0 7M20 6a8.5 8.5 0 0 1 0 12" />
  </Svg>
)

export const IconBell = (p: IconProps) => (
  <Svg {...p}>
    <path d="M18 8.5a6 6 0 0 0-12 0c0 5-2 6.5-2 6.5h16s-2-1.5-2-6.5Z" />
    <path d="M10.3 19a2 2 0 0 0 3.4 0" />
  </Svg>
)

export const IconSettings = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 14.5a1.6 1.6 0 0 0 .32 1.77l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.6 1.6 0 0 0-1.77-.32 1.6 1.6 0 0 0-.97 1.47V21a2 2 0 0 1-4 0v-.1a1.6 1.6 0 0 0-1.05-1.47 1.6 1.6 0 0 0-1.77.32l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.6 1.6 0 0 0 .32-1.77 1.6 1.6 0 0 0-1.47-.97H3a2 2 0 0 1 0-4h.1a1.6 1.6 0 0 0 1.47-1.05 1.6 1.6 0 0 0-.32-1.77l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.6 1.6 0 0 0 1.77.32H9a1.6 1.6 0 0 0 .97-1.47V3a2 2 0 0 1 4 0v.1a1.6 1.6 0 0 0 .97 1.47 1.6 1.6 0 0 0 1.77-.32l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.6 1.6 0 0 0-.32 1.77V9a1.6 1.6 0 0 0 1.47.97H21a2 2 0 0 1 0 4h-.1a1.6 1.6 0 0 0-1.47.97Z" />
  </Svg>
)

export const IconSearch = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="10.5" cy="10.5" r="6.5" />
    <path d="m21 21-5.8-5.8" />
  </Svg>
)

export const IconPlus = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 5v14M5 12h14" />
  </Svg>
)

export const IconSignOut = (p: IconProps) => (
  <Svg {...p}>
    <path d="M9 21H5.5A1.5 1.5 0 0 1 4 19.5v-15A1.5 1.5 0 0 1 5.5 3H9" />
    <path d="M16 16.5 20.5 12 16 7.5M20.5 12H9" />
  </Svg>
)
